import { CATEGORIES } from '@kijiji/category'
import { type PersonalAccount } from '@kijiji/generated/graphql-types'
import { type NextRouter } from 'next/router'
import { type Session } from 'next-auth'

import { ICO_UTM_PARAMS } from '@/constants/ico'
import { PROVINCES_ABBREVIATION } from '@/constants/location'
import { navigateToPAF } from '@/domain/autocanada/navigateToPAF'
import { type ICOOptions } from '@/features/autocanada/types/acIco'
import { type RouteLocale } from '@/hooks/useLocale'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { getAutoCanadaWebLabel } from '@/lib/ga/events/autoCanada'
import { isNumber } from '@/utils/types'

type AttributeParameters = { odometer?: string; vin?: string }

type OpenAutocanValueEstimateModalProps = {
  ctaId: string
  customGAParameters?: Record<string, unknown>
  isMobile: boolean
  onClose?: () => void
  onError?: () => void
  /**
   * Either receive the province abbreviation or the province ID
   * Depending on the data the page has access to
   */
  provinceId?: number
  provinceAbbreviation?: string
  push: NextRouter['push']
  routeLocale: RouteLocale
  /**
   * Either receive user or profile data depending on the data the page has access to
   */
  user?: Session['user']
  profileData?: PersonalAccount
  utmCampaign?: string
  utmContent: string
} & AttributeParameters

export const openAutocanValueEstimateModal = ({
  ctaId,
  customGAParameters,
  isMobile,
  odometer,
  onClose,
  onError,
  profileData,
  provinceAbbreviation,
  provinceId,
  push,
  routeLocale,
  user,
  utmCampaign = ICO_UTM_PARAMS.UTM_CAMPAIGN_ICO,
  utmContent,
  vin,
}: OpenAutocanValueEstimateModalProps): void => {
  let province
  if (provinceAbbreviation) {
    province = provinceAbbreviation
  } else if (provinceId) {
    province = PROVINCES_ABBREVIATION[provinceId]
  }

  const userName = profileData?.name ?? user?.name ?? ''
  const userEmail = profileData?.email ?? user?.email ?? ''
  const userId = profileData?.id ?? user?.sub ?? ''
  const phone = profileData?.phoneNumber ?? ''

  const mileage = parseInt(odometer ?? '')

  const data: ICOOptions['data'] = {
    ctaId,
    province,
    odometer: isNumber(mileage) ? mileage : undefined,
    vin,
    email: userEmail,
    firstName: userName?.split(' ')[0] ?? '',
    lastName: userName?.split(' ').pop() ?? '',
    phone,
    userId,
  }
  const utmParams: ICOOptions['utmParams'] = {
    utmContent,
    utmCampaign,
    utmSource: ICO_UTM_PARAMS.UTM_SOURCE,
    utmMedium: isMobile ? ICO_UTM_PARAMS.UTM_MEDIUM_MWEB : ICO_UTM_PARAMS.UTM_MEDIUM_WEB,
  }

  const modalParameters: ICOOptions = {
    data,
    locale: routeLocale,
    utmParams,
    onSubmittedForEstimate: () => {
      trackEvent({
        action: GA_EVENT.ValueEstimateComplete,
        label: getAutoCanadaWebLabel(),
        customParameters: customGAParameters,
      })
    },
    onIcoPostToKijiji: () => navigateToPAF(push, CATEGORIES.CARS_AND_TRUCKS_CATEGORY_ID),
    onClose,
    onError,
  }

  // Initialize the value estimate modal from AutoCanada's SDK.
  global.window.acIco?.initModal('value-estimate', 'kijiji', modalParameters)
}
